import { useViewershipPlatformsByDares, } from 'root/src/client/logic/dropEvent/hooks/useViewershipPlatformsByDares';
import { useUserCreatorData } from 'root/src/client/logic/auth/hooks/useUser';
import { useDropEvent, useUserCampaignData } from 'root/src/client/logic/dropEvent/hooks/useDropEvent';
import { calculateMaxDaresByPlatform, } from 'root/src/shared/util/calculateMaxDares';
import { DarePlatform } from 'root/src/shared/@types/platforms';
import { useMaxDaresByPlatforms } from 'root/src/client/logic/dropEvent/hooks/useMaxDaresByPlatforms';
import { formatNumberWithCommas } from 'root/src/shared/util/formatNumberWithCommas';
import { useIsBrand } from 'root/src/client/v2/auth/hooks/useIsBrand';
import { useIsAdmin } from 'root/src/client/v2/auth/hooks/useIsAdmin';
import { useIsAuthenticated } from 'root/src/client/v2/auth/hooks/useIsAuthenticated';
import { isString } from 'remeda';
import { getReadableViewershipPlatform } from 'root/src/shared/platforms/helpers/getReadablePlatform';
const CCV_REQUIRED_OFFSET = 1;
export const useDareRequirementMessages = () => {
    const { viewershipPlatforms, averageCcv = 0, averageTikTokViews = 0, averageYoutubeViews = 0, averageYoutubeShortsViews = 0, } = useUserCreatorData();
    const dropEvent = useDropEvent();
    const userCampaignData = useUserCampaignData(dropEvent === null || dropEvent === void 0 ? void 0 : dropEvent.mnemonicId);
    const { isBrand } = useIsBrand();
    const { isAdmin } = useIsAdmin();
    const isAuthenticated = useIsAuthenticated();
    const { viewershipPlatformsByDares } = useViewershipPlatformsByDares();
    const maxDaresByUserPlatforms = useMaxDaresByPlatforms();
    if (!dropEvent) {
        return [];
    }
    if (!dropEvent.dares.length) {
        return [];
    }
    if (!isAuthenticated) {
        return ['Sign in to get started'];
    }
    if (isBrand || isAdmin) {
        return [];
    }
    if (!viewershipPlatforms.length) {
        return ['Connect a channel to get started.'];
    }
    if (maxDaresByUserPlatforms === 0) {
        const minimalRequirementsMessage = getMinimalRequirementsMessage({
            viewershipPlatforms: viewershipPlatformsByDares,
            dropEvent,
            averageCcv,
            averageTikTokViews,
            averageYoutubeShortsViews,
            averageYoutubeViews,
        });
        if (!minimalRequirementsMessage) {
            return [];
        }
        return [minimalRequirementsMessage];
    }
    return viewershipPlatformsByDares
        .map((platform) => {
        const maxDares = calculateMaxDaresByPlatform({
            averageCcv,
            averageYoutubeViews,
            averageYoutubeShortsViews,
            averageTikTokViews,
            platform,
            campaign: dropEvent,
        });
        return getDareMessageByPlatform({
            maxDares,
            dropEvent,
            viewershipPlatforms,
            platform,
            creatorAcceptedDaresByPlatform: (userCampaignData === null || userCampaignData === void 0 ? void 0 : userCampaignData.creatorAcceptedDaresByPlatform) || {},
        });
    })
        .filter(isString);
};
export function getDareMessageByPlatform({ platform, dropEvent, maxDares, viewershipPlatforms, creatorAcceptedDaresByPlatform, }) {
    var _a;
    if (!dropEvent) {
        return null;
    }
    const isBreakpoints = Boolean(dropEvent.viewershipBreakpoints);
    if (!viewershipPlatforms.includes(platform)) {
        return null;
    }
    if (platform === DarePlatform.Twitch && maxDares === Infinity) {
        return 'Based on your Twitch viewership, you can accept ALL Twitch dares.';
    }
    if (platform === DarePlatform.YouTube && maxDares === Infinity) {
        return 'Based on your YouTube viewership, you can accept ALL YouTube dares.';
    }
    if (platform === DarePlatform.YouTubeShorts && maxDares === Infinity) {
        return 'Based on your YouTube Videos viewership, you can accept ALL YouTube Videos dares.';
    }
    if (platform === DarePlatform.TikTok && maxDares === Infinity) {
        return 'Based on your TikTok viewership, you can accept ALL TikTok dares.';
    }
    if (!isBreakpoints) {
        return null;
    }
    const isAllDares = !Number.isFinite(maxDares);
    const left = Math.max(0, maxDares - ((_a = creatorAcceptedDaresByPlatform[platform]) !== null && _a !== void 0 ? _a : 0));
    return `Based on your ${getReadableViewershipPlatform(platform)} viewership, you can accept ${isAllDares ? 'ALL' : maxDares} ${getReadableViewershipPlatform(platform)} dares.${isAllDares ? '' : ` (${left} left)`}`;
}
export function getMinimalRequirementsMessage({ dropEvent, viewershipPlatforms, averageCcv, averageYoutubeViews, averageYoutubeShortsViews, averageTikTokViews, }) {
    const { viewershipBreakpoints } = dropEvent;
    const minCcv = getMinBreakpoint(viewershipBreakpoints === null || viewershipBreakpoints === void 0 ? void 0 : viewershipBreakpoints.twitch, CCV_REQUIRED_OFFSET);
    const minYoutubeViews = getMinBreakpoint(viewershipBreakpoints === null || viewershipBreakpoints === void 0 ? void 0 : viewershipBreakpoints.youtube);
    const minYoutubeShortsViews = getMinBreakpoint(viewershipBreakpoints === null || viewershipBreakpoints === void 0 ? void 0 : viewershipBreakpoints.youtubeShorts);
    const minTikTokAvgViews = getMinBreakpoint(viewershipBreakpoints === null || viewershipBreakpoints === void 0 ? void 0 : viewershipBreakpoints.tiktok);
    const userRequirementsMessages = viewershipPlatforms
        .map((platform) => {
        if (platform === DarePlatform.Twitch && minCcv && minCcv > (averageCcv || 0)) {
            return `${formatNumberWithCommas(minCcv)} Twitch CCV`;
        }
        if (platform === DarePlatform.YouTube && minYoutubeViews && minYoutubeViews > averageYoutubeViews) {
            return `${formatNumberWithCommas(minYoutubeViews)} Average YouTube Videos Views`;
        }
        if (platform === DarePlatform.YouTubeShorts && minYoutubeShortsViews && minYoutubeShortsViews > averageYoutubeShortsViews) {
            return `${formatNumberWithCommas(minYoutubeShortsViews)} Average YouTube Shorts Views`;
        }
        if (platform === DarePlatform.TikTok && minTikTokAvgViews && minTikTokAvgViews > averageTikTokViews) {
            return `${formatNumberWithCommas(minTikTokAvgViews)} Average TikTok Views`;
        }
        return null;
    })
        .filter(item => item);
    const canUserParticipate = userRequirementsMessages.length !== viewershipPlatforms.length;
    if (!canUserParticipate) {
        return `You need at least ${userRequirementsMessages.join(' or ')} to participate in this drop.`;
    }
    return null;
}
export function getMinBreakpoint(breakpoints, offset = 0) {
    const countBreakpoints = breakpoints === null || breakpoints === void 0 ? void 0 : breakpoints.map(({ viewershipCount }) => viewershipCount);
    if (!(countBreakpoints === null || countBreakpoints === void 0 ? void 0 : countBreakpoints.length)) {
        return null;
    }
    return Math.min(...countBreakpoints) + offset;
}
